import { compact, includes, isEmpty, map, omit, trimEnd } from 'lodash';
import { isNil, omitBy, toString } from 'lodash/fp';
import 'match-media';
import { createHistoryMiddleware, FeatureFlag } from '@mews-distributor/common';
import { createRepositoryRegistry } from '@mews-distributor/data-access';
import { Application, PlatformEnvironment, Team } from '@mews/enums';
import {
    AssetLoader,
    createAzureAppInsightsLoggerAdapter,
    createConsoleLoggerAdapter,
    createCoralogixLoggerAdapter,
    createEnvironmentService,
    createFakeNewRelicReporterService,
    createFeatureFlagService,
    createGainsightService,
    createLoggerService,
    createNewRelicLoggerAdapter,
    createNewRelicReporterService,
    createRecaptchaService,
    createSentryMiddleware,
    createSentryReporterService,
    createTrackingConsentService,
    isNewRelicLoggerActive,
    isNewRelicReporterActive,
    setSentryTags,
} from '@mews/framework';
import { createAppClockService, Date, DateTime, DateTimeService, DateTimeUtcService } from '@mews/framework/datetime';
import { createPciProxyService } from '@mews/framework/payments';
import { isIframed } from '@mews/framework/utils';
import { createUUID } from '@mews/std';
import { createApiService } from './api/apiService/createApiService';
import { getRequestCredentials } from './api/apiService/credentials';
import { appConfigurationSchema } from './bootstrap/appConfigurationSchema';
import { DEFAULT_TRACKING_CONSENT } from './bootstrap/bootstrapConstants';
import { configureModuleManager } from './configureModuleManager';
import { configureStore } from './configureStore';
import { ALLOWED_CUSTOM_OPTIONS, DEFAULT_CURRENCY_CODE, DEFAULT_LANGUAGE_CODE } from './constants';
import { closeMiddleware } from './middlewares/closeMiddleware';
import { integrationsMiddleware } from './middlewares/integrationsMiddleware/integrationsMiddleware';
import { getCurrencyCode, getCurrentRoute, getLanguageCode, getSelectedCityId } from './modules/app/selectors';
import {
    getConfigurationOrHotelIds,
    getDistributorApiUrl,
    getGeneralApiUrl,
    getVersionedClientName,
} from './modules/configuration/selectors';
import { getCityEnterpriseTimezone, getEnterpriseTimezone } from './modules/entities/enterprises/selectors';
import { setConfiguration } from './modules/global/actions';
import { prepareGlobalization } from './modules/globalization/prepareGlobalization';
import {
    getCurrentDateTimeLocalizationInfo,
    hasTextTranslation,
    localizeCurrencyValue,
    localizeDate,
    localizePercent,
    localizeProperty,
    localizeShortDate,
    localizeShortTime,
    localizeText,
} from './modules/globalization/selectors';
import { getSelectedHotelId } from './modules/resourceOrder/selectors';
import { createApolloClient } from './modules/graphql/createApolloClient';
import { reducers } from './reducers/reducers';
import { renderApp } from './render';
import { handleAzureAppInsightsError } from './services/azureAppInsightsLoggerAdapter';
import { createDistributorCalendarService } from './services/createDistributorCalendarService';
import { createDistributorHistoryService } from './services/createDistributorHistoryService';

import { createReactQueryClient } from './services/reactQuery/createReactQueryClient';
import { generateTrackingConsents } from './services/trackingConsents/generateTrackingConsents';
import { createWidgetDistributorTrackingConsentsService } from './services/trackingConsents/widgetDistributorTrackingConsentsService';
import { createApiObject } from './utils/createApiObject';
import { logIframedUrl } from './utils/logging';
import { createSession, createSessionToken } from './utils/session/session';
import { getSentryIgnoreErrors, logSentryError } from './sentry/sentry';

export const distributorApp = (
    {
        element,
        contextWindow = global,
        closeApp = () => {},
        configuration,
        globalization,
        customOptions,
        logsFromLoader = [],
        setHtmlLanguage = () => {},
    }: $TSFixMe,
    initialState: $TSFixMe
) => {
    const { languageCode } = initialState.app;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (languageCode) {
        const knownLanguageCodes = map(globalization.languages, (l) => l.code);
        if (!includes(knownLanguageCodes, languageCode)) {
            initialState.app.languageCode = DEFAULT_LANGUAGE_CODE;
        }
    }

    const { currencyCode } = initialState.app;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (currencyCode) {
        const knownCurrencyCodes = map(globalization.currencies, (l) => l.code);
        if (!includes(knownCurrencyCodes, currencyCode)) {
            initialState.app.currencyCode = DEFAULT_CURRENCY_CODE;
        }
    }
    // preprocess globalization for saving in reducer
    initialState.globalization = prepareGlobalization(globalization);

    const { app: appConfiguration } = configuration;
    const {
        applicationInsightsInstrumentationKey,
        environment,
        newRelic,
        isStandalone,
        platformAssetUrl,
        platformUrls,
        sentryDsn,
        launchDarklyToken,
        launchDarklyOverrides,
        gainsightToken,
        launchDarklyFlagDefaultValue = false,
        bookingEngineGraphQLUrl,
        pciProxySecureFieldsScriptUrl,
    } = appConfiguration;

    const featureFlagService = createFeatureFlagService({
        apiKey: launchDarklyToken,
        flagDefaultValue: launchDarklyFlagDefaultValue,
        configFlagOverrides: launchDarklyOverrides,
        flagOverrides: initialState.app.featureFlagDebug,
    });

    const pciProxyService = createPciProxyService({ scriptUrl: pciProxySecureFieldsScriptUrl });

    const environmentService = createEnvironmentService({ context: contextWindow });

    const iframed = isIframed(contextWindow);

    let sentryMiddleware;
    let sentryReporterService: $TSFixMe;

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!__LOCALHOST__ && !!sentryDsn) {
        const { scriptOriginUrls } = appConfiguration;

        const sentryProps = {
            dsn: sentryDsn,
            environment,
            release: `mews-distributor@${__VERSION__}`,
        };

        sentryReporterService = createSentryReporterService(sentryProps);
        sentryMiddleware = createSentryMiddleware({
            ...sentryProps,
            tracesSampler: ({ _name, _attributes, _inheritOrSampleWith }) => 1.0,
            shouldRecordErrorSessions: true,
            allowUrls: scriptOriginUrls,
            sentryReporterService,
            ignoreErrors: getSentryIgnoreErrors(isStandalone, iframed),
        });
    }

    let store: $TSFixMe;

    const { nowUtc } = appConfiguration;
    const appClockService = createAppClockService({
        context: contextWindow,
        getServerNowUtc: () => nowUtc,
    });

    const distributorHistoryService = createDistributorHistoryService({ environmentService });

    const sessionId = createUUID();
    initialState.app.sessionId = sessionId;

    const { configurationIds } = configuration.settings;
    const contextHref = contextWindow.location.href;

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const configurationIdsString = configurationIds ? toString(configurationIds) : null;

    const azureLoggerAdapter =
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        !__LOCALHOST__ && applicationInsightsInstrumentationKey
            ? createAzureAppInsightsLoggerAdapter({
                  applicationInsightsInstrumentationKey,
                  getNowUTC: () => DateTimeUtcService.toISOString(appClockService.getAppNowUtc()),
                  onError: handleAzureAppInsightsError(sentryReporterService),
                  sessionOptions: {
                      sessionId,
                      configurationIds,
                      contextHref,
                  },
              })
            : null;

    const newRelicAdapter = isNewRelicLoggerActive(newRelic)
        ? createNewRelicLoggerAdapter({
              apiEndpoint: newRelic?.logEndpoint,
              apiInsertKey: newRelic?.insertKey,
              getTimestamp: appClockService.getValue,
              commonAttributes: {
                  team: Team.Guests,
                  environment,
                  app: {
                      name: Application.BookingEngine,
                      version: __VERSION__,
                  },
                  sessionId,
                  configurationIds: configurationIdsString,
                  contextHref,
              },
              sentryReporterService,
          })
        : null;

    const coralogixLoggerAdapter = featureFlagService.hasFlag(FeatureFlag.bookingEngineCoralogixLoggingEnabled)
        ? createCoralogixLoggerAdapter()
        : null;

    const consoleLogAdapter = environment !== PlatformEnvironment.Production ? createConsoleLoggerAdapter() : null;

    /**
     * We maintain two separate logger services because we no longer want New Relic to receive `gtmEvent` logs.
     *
     * - `loggerService` includes New Relic and is used for general system logging.
     * - `azureLoggerService` excludes New Relic and is used in the Azure-to-Databricks integration,
     *   where `gtmEvent` logs are still required.
     *
     * This separation prevents redundant logging and reduced daily log volume by approximately 7.3 million entries at the time when it was added.
     *
     * More context in: https://github.com/MewsSystems/mews-js/pull/25437
     */
    const loggerService = createLoggerService({
        loggerAdapters: compact([azureLoggerAdapter, newRelicAdapter, consoleLogAdapter, coralogixLoggerAdapter]),
    });

    const azureLoggerService = createLoggerService({
        loggerAdapters: compact([azureLoggerAdapter, consoleLogAdapter]),
    });

    logsFromLoader.forEach((log: { type: string; message: string; data: object }) => {
        loggerService.log(log.type, log.message, log.data);
    });

    const isPlatformOrigin = platformUrls?.includes(contextWindow.location.origin) === true;
    const trackingConsentsService =
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        isStandalone && (__LOCALHOST__ || isPlatformOrigin)
            ? createTrackingConsentService({ context: contextWindow })
            : createWidgetDistributorTrackingConsentsService({
                  defaultConsents: generateTrackingConsents(DEFAULT_TRACKING_CONSENT),
              });

    const gainsightService = createGainsightService(window, gainsightToken, {
        loggerService,
        iframeModeEnabled: false,
    });

    const recaptchaService = createRecaptchaService(appConfiguration.googleRecaptchaSiteKey, window);

    const middlewares = compact([
        integrationsMiddleware({
            context: contextWindow,
            trackingConsentsService,
            sentryReporterService,
            loggerService,
            azureLoggerService,
            appClockService,
            gainsightService,
        }),
        createHistoryMiddleware({
            getDocumentTitle: () => environmentService.get('document').title,
            history: contextWindow.history,
        }),
        closeMiddleware(closeApp),
        sentryMiddleware,
    ]);

    const sessionToken = createSessionToken({ nowUtc });
    const apiService = createApiService({
        environment,
        getDistributorBaseUrl: () => getDistributorApiUrl(store.getState()),
        getGeneralBaseUrl: () => getGeneralApiUrl(store.getState()),
        getClientName: () => getVersionedClientName(store.getState()),
        getSessionToken: () => sessionToken,
    });

    const getCommonParams = () => {
        const clientName = getVersionedClientName(store.getState());

        return {
            client: clientName,
            session: createSession({ sessionToken, clientName }),
            // TODO: Remove when backend is typed properly | https://mews.myjetbrains.com/youtrack/issue/RND-134781/Get-rid-of-accessToken-annotation-on-Private-Booking-Engine-API
            accessToken: '',
        };
    };

    const credentials = getRequestCredentials(environment);
    const repositories = createRepositoryRegistry({
        getDistributorOptions: () => ({
            baseUrl: getDistributorApiUrl(store.getState()),
            commonParams: getCommonParams(),
            credentials,
            correlationId: createUUID(),
        }),
        getGeneralOptions: () => ({
            baseUrl: getGeneralApiUrl(store.getState()),
            commonParams: getCommonParams(),
            credentials,
        }),
    });

    const calendarService = createDistributorCalendarService({
        getLocalization: () => getCurrentDateTimeLocalizationInfo(store.getState()),
        getCurrentDate: () => {
            const state = store.getState();
            const enterpriseId = getSelectedHotelId(state);
            let timezone: string | undefined;

            if (enterpriseId != null) {
                timezone = getEnterpriseTimezone(state, { enterpriseId });
            } else {
                // For multiproperty booking engine read timezone of the selected city
                const cityId = getSelectedCityId(state);
                timezone = getCityEnterpriseTimezone(state, { cityId });
            }

            const currentUtc = appClockService.getAppNowUtc();
            const dateTime = DateTimeService.fromUtc(currentUtc, timezone ?? 'Etc/GMT'); // can't use UTC as it is not supported by time library

            return {
                year: DateTimeService.getYear(dateTime),
                month: DateTimeService.getMonth(dateTime),
                date: DateTimeService.getDate(dateTime),
            };
        },
    });

    const platformImageAssetLoader = new AssetLoader({
        baseUrl: platformAssetUrl,
        assetDirectory: 'images',
    });

    const moduleManager = configureModuleManager({
        initialReducers: reducers,
        errorReporterService: sentryReporterService,
    });

    const newRelicReporterService = isNewRelicReporterActive(newRelic)
        ? createNewRelicReporterService({
              apiEndpoint: newRelic?.eventEndpoint,
              apiInsertKey: newRelic?.insertKey,
              getTimestamp: appClockService.getValue,
              sessionId,
              sentryReporterService,
          })
        : createFakeNewRelicReporterService();

    // Create a temporary store reference that will be assigned after store initialization.
    // This is necessary because Apollo Client's getCustomHeaders callback needs access to store.getState()
    // during query execution, but the store cannot be created until Apollo Client is configured.
    // This circular dependency is resolved by:
    // 1. Creating Apollo Client with a callback that references storeRef
    // 2. Creating the store with the configured Apollo Client
    // 3. Assigning the created store to storeRef
    let storeRef: ReturnType<typeof configureStore> | null = null;

    const apolloClient =
        bookingEngineGraphQLUrl != null && bookingEngineGraphQLUrl !== ''
            ? createApolloClient({
                  graphQLUrl: bookingEngineGraphQLUrl,
                  environment,
                  getCustomHeaders: () => {
                      if (storeRef == null) {
                          return {};
                      }
                      const clientName = getVersionedClientName(storeRef.getState());
                      const commonParams = {
                          client: clientName,
                          session: createSession({ sessionToken, clientName }),
                          accessToken: '',
                      };
                      const configurationOrHotelIds = getConfigurationOrHotelIds(storeRef.getState()).join(',');

                      return omitBy(isNil)({
                          'X-Mews-Client': commonParams.client,
                          'X-Mews-SessionId': commonParams.session,
                          'X-Mews-BookingEngineConfigurationIds': configurationOrHotelIds,
                          'X-Mews-Language': getLanguageCode(storeRef.getState()),
                          'X-Mews-Currency': getCurrencyCode(storeRef.getState()),
                          'X-Mews-CorrelationId': createUUID(),
                      });
                  },
                  onQueryCalled: (data) => {
                      if (!featureFlagService.hasFlag(FeatureFlag.bookingEngineGraphqlLoggingEnabled)) {
                          return;
                      }
                      loggerService.log('graphql-request', 'GraphQL operation requested', {
                          ...data,
                          urlHref: contextWindow.location.href,
                          origin: contextWindow.origin,
                      });
                  },
                  onQueryError: (data) => {
                      loggerService.log('graphql-request-error', 'GraphQL operation failed with error', {
                          ...data,
                          urlHref: contextWindow.location.href,
                          origin: contextWindow.origin,
                      });
                  },
              })
            : null;

    store = configureStore({
        initialState,
        configuredMiddlewares: middlewares,
        contextWindow,
        apolloClient,
        apiService,
        appClockService,
        calendarService,
        environmentService,
        historyService: distributorHistoryService.historyService,
        loggerService,
        pciProxyService,
        platformImageAssetLoader,
        trackingConsentsService,
        sentryReporterService,
        featureFlagService,
        moduleManager,
        newRelicReporterService,
        gainsightService,
        setHtmlLanguage,
        repositories,
        recaptchaService,
    });

    // Assign store reference for Apollo Client's getCustomHeaders
    storeRef = store;

    try {
        appConfigurationSchema.parse(appConfiguration);
    } catch (error) {
        store.dispatch(logSentryError({ error }));
    }

    moduleManager.setDispatch(store.dispatch);
    distributorHistoryService.setStore(store);
    const { dispatch } = store;

    dispatch(
        setSentryTags({
            'context.href': contextHref,
            sessionId,
        })
    );

    dispatch(setConfiguration(configuration));

    // logging
    const urlProtocol = trimEnd(contextWindow.location.protocol, ':');
    if (urlProtocol.toLowerCase() !== 'https') {
        loggerService.log('protocol', 'Protocol scheme of the URL is not HTTPS', { protocol: urlProtocol });
    }

    if (iframed) {
        logIframedUrl({ contextWindow, loggerService });
    }
    initialState.app.isInvalidIframe = iframed && !isPlatformOrigin && !__LOCALHOST__;

    const filteredCustomOptions = omit(customOptions, ALLOWED_CUSTOM_OPTIONS);
    if (!isEmpty(filteredCustomOptions)) {
        loggerService.log('custom-options', 'Distributor custom options', {
            options: filteredCustomOptions,
        });
    }

    const checkTranslation = (textKey: $TSFixMe, lCode: $TSFixMe) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (textKey && !hasTextTranslation(store.getState(), { languageCode: lCode, textKey })) {
            loggerService.log(
                'missing-translation',
                `Translation for key: ${textKey} in languageCode: ${lCode} is missing`,
                { languageCode: lCode, textKey }
            );
        }
    };

    const localizationHandlers = {
        localizeText: (lCode: $TSFixMe, textKey: $TSFixMe, ...args: $TSFixMe[]) => {
            checkTranslation(textKey, lCode);
            // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
            return localizeText(textKey, ...args)(store.getState(), { languageCode: lCode });
        },
        localizeProperty: (__: $TSFixMe, property: $TSFixMe) => localizeProperty(store.getState(), { property }),
        localizeShortDate: (_languageCode: string, date: Date) => localizeShortDate(store.getState(), { date }),
        localizeShortTime: (_languageCode: string, dateTime: DateTime) =>
            localizeShortTime(store.getState(), { dateTime }),
        localizeDate: (_languageCode: $TSFixMe, date: $TSFixMe) => localizeDate(store.getState(), { date }),
        localizeCurrencyValue: (
            currencyValueLanguageCode: $TSFixMe,
            currencyValue: $TSFixMe,
            options = {},
            optionalCurrencyCode = null
        ) => {
            try {
                return localizeCurrencyValue(store.getState(), {
                    languageCode: currencyValueLanguageCode,
                    currencyValue,
                    pricingMode: (options as $TSFixMe).pricingMode,
                    optionalCurrencyCode,
                });
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (sentryReporterService) {
                    sentryReporterService.logError({
                        error,
                        extra: {
                            step: {
                                routeName: getCurrentRoute(store.getState()),
                                step: 'localizeCurrencyValue-handler',
                            },
                            currencyValueLanguageCode,
                            currencyValue,
                            options,
                            optionalCurrencyCode,
                            selectedCurrencyCode: getCurrencyCode(store.getState()),
                        },
                    });
                    return null;
                }
                throw error;
            }
        },
        localizePercent: (langCode: $TSFixMe, number: $TSFixMe, options = {}) =>
            // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
            localizePercent(store.getState(), { cultureCode: langCode, number, options }),
    };

    const reactQueryClient = createReactQueryClient({ dispatch: store.dispatch });

    renderApp({
        store,
        contextWindow,
        localizationHandlers,
        platformImageAssetLoader,
        element,
        featureFlagService,
        appClockService,
        calendarService,
        moduleManager,
        loggerService,
        apolloClient,
        reactQueryClient,
        repositories,
        gainsightService,
        recaptchaService,
    });

    return createApiObject(store, { appClockService });
};
